import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames/bind';
import { useMediaQuery } from "../hooks";
import { ClickOutside } from "../internal/ClickOutside";
import { DropListDesktop, DropListMobile } from "../internal/DropList";
import noop from "../utils/noop";
import { filterByTerm, truncateText } from "../utils/string";
import FilterButton from '../FilterButton';
import styles from './Filter.css';
var cx = classNames.bind(styles);
var defaultOptionItem = {
  label: 'Default label',
  value: 'default_value'
};

var hasOptionInSelectedOptions = function hasOptionInSelectedOptions(selectedOptions, option) {
  return selectedOptions.some(function (item) {
    return item.value === option.value;
  });
};

var calcTitleAndCompleted = function calcTitleAndCompleted(selected, title, showAloneValueTitle) {
  if (selected.length > 1) {
    return ["".concat(title, ": ").concat(selected.length), true];
  }

  if (selected.length === 1) {
    var label = selected[0].label;
    return [showAloneValueTitle ? "".concat(title, ": ").concat(label) : label, true];
  }

  return [title, false];
};

var Filter = function Filter(_ref) {
  var block = _ref.block,
      _ref$confirmLabel = _ref.confirmLabel,
      confirmLabel = _ref$confirmLabel === void 0 ? 'Готово' : _ref$confirmLabel,
      disabled = _ref.disabled,
      icon = _ref.icon,
      items = _ref.items,
      _ref$multiple = _ref.multiple,
      multiple = _ref$multiple === void 0 ? false : _ref$multiple,
      _ref$onChange = _ref.onChange,
      onChange = _ref$onChange === void 0 ? noop : _ref$onChange,
      _ref$theme = _ref.theme,
      theme = _ref$theme === void 0 ? 'b2c' : _ref$theme,
      _ref$title = _ref.title,
      title = _ref$title === void 0 ? 'Фильтр' : _ref$title,
      _ref$maxTextLength = _ref.maxTextLength,
      maxTextLength = _ref$maxTextLength === void 0 ? 25 : _ref$maxTextLength,
      _ref$withoutScrollBar = _ref.withoutScrollBar,
      withoutScrollBar = _ref$withoutScrollBar === void 0 ? false : _ref$withoutScrollBar,
      maxVisibleItems = _ref.maxVisibleItems,
      searchable = _ref.searchable,
      _ref$zIndex = _ref.zIndex,
      zIndex = _ref$zIndex === void 0 ? 999 : _ref$zIndex,
      _ref$listWidth = _ref.listWidth,
      listWidth = _ref$listWidth === void 0 ? 200 : _ref$listWidth,
      _ref$noOptionsMessage = _ref.noOptionsMessage,
      noOptionsMessage = _ref$noOptionsMessage === void 0 ? 'No options' : _ref$noOptionsMessage,
      propsValue = _ref.value,
      showAloneValueTitle = _ref.showAloneValueTitle,
      isSorting = _ref.isSorting,
      _ref$sortingTitle = _ref.sortingTitle,
      sortingTitle = _ref$sortingTitle === void 0 ? 'Sorting by' : _ref$sortingTitle,
      _ref$defaultItem = _ref.defaultItem,
      defaultItem = _ref$defaultItem === void 0 ? defaultOptionItem : _ref$defaultItem,
      _ref$placement = _ref.placement,
      placement = _ref$placement === void 0 ? 'bottomLeft' : _ref$placement,
      _ref$locale = _ref.locale,
      locale = _ref$locale === void 0 ? 'ru' : _ref$locale,
      onChangeSearch = _ref.onChangeSearch;
  var breakpoints = useMediaQuery('xs');
  var isMobile = breakpoints.xs;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isOpen = _useState2[0],
      setIsOpen = _useState2[1];

  var _useState3 = useState([]),
      _useState4 = _slicedToArray(_useState3, 2),
      selected = _useState4[0],
      setSelected = _useState4[1];

  var _useState5 = useState(''),
      _useState6 = _slicedToArray(_useState5, 2),
      searchTerm = _useState6[0],
      setSearchTerm = _useState6[1];

  var _useState7 = useState([]),
      _useState8 = _slicedToArray(_useState7, 2),
      hasSubItems = _useState8[0],
      setHasSubItems = _useState8[1];

  useEffect(function () {
    setHasSubItems(selected.filter(function (item) {
      return !!item.subItems;
    }));
  }, [selected]);
  var buttonRef = useRef(null);

  var _calcTitleAndComplete = calcTitleAndCompleted(selected, title, showAloneValueTitle),
      _calcTitleAndComplete2 = _slicedToArray(_calcTitleAndComplete, 2),
      buttonTitle = _calcTitleAndComplete2[0],
      completed = _calcTitleAndComplete2[1];

  var finalListWidth = 'auto';

  if (block) {
    finalListWidth = '100%';
  } else if (listWidth) {
    finalListWidth = "".concat(listWidth, "px");
  }

  useEffect(function () {
    if (propsValue) {
      var values = Array.isArray(propsValue) ? propsValue : [].concat(propsValue);
      var selectedOptions = items.filter(function (item) {
        return values.some(function (el) {
          return el.value === item.value;
        });
      });
      var selectedSubOptions = [];
      items.forEach(function (item) {
        if (item.subItems) {
          var _item$subItems;

          selectedSubOptions.push.apply(selectedSubOptions, _toConsumableArray((_item$subItems = item.subItems) === null || _item$subItems === void 0 ? void 0 : _item$subItems.options.filter(function (subOption) {
            return values.some(function (el) {
              return el.value === subOption.value;
            });
          })));
        }
      });
      setSelected([].concat(_toConsumableArray(selectedOptions), selectedSubOptions));
    }
  }, [propsValue, items]);
  useEffect(function () {
    setSearchTerm('');
  }, [isOpen]);
  var toggleFiltersList = useCallback(function () {
    setIsOpen(!isOpen);
  }, [isOpen]);
  var closeFiltersList = useCallback(function () {
    setIsOpen(false);
  }, []);
  var handleFiltersListClickOutside = useCallback(function (_ref2) {
    var _buttonRef$current;

    var target = _ref2.target;

    if (!((_buttonRef$current = buttonRef.current) !== null && _buttonRef$current !== void 0 && _buttonRef$current.contains(target))) {
      closeFiltersList();
    }
  }, [buttonRef, closeFiltersList]);
  var handleSelectListItem = useCallback(function (option) {
    if (multiple) {
      var selectedOption = hasOptionInSelectedOptions(selected, option) ? selected.filter(function (_ref3) {
        var value = _ref3.value;
        return value !== option.value;
      }) : selected.concat(option);
      var unSelectedSubOptions = [];

      if (selected.includes(option) && option.subItems) {
        unSelectedSubOptions.push.apply(unSelectedSubOptions, _toConsumableArray(option.subItems.options.filter(function (subOption) {
          return selected.some(function (el) {
            return el.value === subOption.value;
          });
        })));
      }

      var compareOptionsNSubOptions = selectedOption.reduce(function (acc, curr) {
        if (!unSelectedSubOptions.includes(curr)) {
          acc.push(curr);
        }

        return acc;
      }, []);
      setSelected(compareOptionsNSubOptions);

      if (onChange) {
        onChange(compareOptionsNSubOptions);
      }
    } else {
      closeFiltersList();
      setSelected([option]);

      if (onChange) {
        onChange(option);
      }
    }
  }, [multiple, selected, closeFiltersList, onChange]);
  var handleClear = useCallback(function () {
    setSelected([]);

    if (onChange) {
      onChange(null);
    }
  }, [onChange]);
  var handleSearch = useCallback(function (value) {
    setSearchTerm(value);

    if (onChangeSearch) {
      onChangeSearch(value);
    }
  }, [onChangeSearch]);
  var filteredItems = useMemo(function () {
    var subItems = searchTerm ? hasSubItems.map(function (item) {
      var _item$subItems2;

      return (_item$subItems2 = item.subItems) === null || _item$subItems2 === void 0 ? void 0 : _item$subItems2.options;
    }).flat() : [];
    return searchTerm ? filterByTerm([].concat(_toConsumableArray(items), _toConsumableArray(subItems)), searchTerm, 'label') : items;
  }, [items, searchTerm, hasSubItems]);

  var handleUnSortClick = function handleUnSortClick() {
    onChange([]);
    setIsOpen(false);
  };

  var Desktop = /*#__PURE__*/React.createElement("div", {
    className: cx('list', "placement_".concat(placement), {
      block: block
    }),
    style: {
      zIndex: zIndex,
      width: finalListWidth
    }
  }, /*#__PURE__*/React.createElement(ClickOutside, {
    ignoreRefs: [buttonRef],
    onClickOutside: handleFiltersListClickOutside
  }, /*#__PURE__*/React.createElement(DropListDesktop, {
    icon: icon,
    items: filteredItems,
    multiple: multiple,
    onSelect: handleSelectListItem,
    onSearch: handleSearch,
    scrollable: withoutScrollBar,
    selected: selected.length ? selected : [defaultItem],
    theme: theme,
    maxVisibleItems: maxVisibleItems,
    searchable: searchable,
    noOptionsMessage: noOptionsMessage,
    hasSubItems: searchTerm ? [] : hasSubItems,
    defaultOptionItem: defaultItem,
    defaultOptionOnClick: handleUnSortClick,
    isSorting: isSorting,
    locale: locale
  })));
  var Mobile = /*#__PURE__*/React.createElement(DropListMobile, {
    theme: theme,
    opened: isOpen,
    title: title,
    multiple: multiple,
    confirmLabel: confirmLabel,
    searchable: searchable,
    onSearch: handleSearch,
    items: filteredItems,
    selected: selected.length ? selected : [defaultItem],
    icon: icon,
    onSelect: handleSelectListItem,
    maxVisibleItems: maxVisibleItems,
    noOptionsMessage: noOptionsMessage,
    scrollable: true,
    onClickOutside: handleFiltersListClickOutside,
    hasSubItems: hasSubItems,
    defaultOptionItem: defaultItem,
    defaultOptionOnClick: handleUnSortClick,
    isSorting: isSorting,
    locale: locale
  });
  return /*#__PURE__*/React.createElement("div", {
    className: cx('root', {
      block: block
    })
  }, /*#__PURE__*/React.createElement(FilterButton, {
    ref: buttonRef,
    active: isOpen,
    block: block,
    disabled: disabled,
    onClear: handleClear,
    onClick: toggleFiltersList,
    theme: theme,
    completed: completed,
    selected: selected[0],
    isSorting: isSorting,
    sortingTitle: sortingTitle
  }, truncateText(buttonTitle, maxTextLength)), isOpen && (isMobile ? Mobile : Desktop));
};

export default Filter;